import React, { useEffect, useState, forwardRef, useRef, useImperativeHandle } from 'react';
import { FormFieldConfig } from '../../Hooks/useForm';
import { getGuid } from '../../Utils/stringUtils';
import './biz-input.scss';
import { BizControlSize } from '../../Types/controlSize';

export type BizInputProps = {
	size?: BizControlSize;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	value: string;
	name?: string;
	placeholder?: string;
	className?: string;
	formConfig?: FormFieldConfig<string>;
	type?: React.HTMLInputTypeAttribute;
	readonly?: boolean;
	legacy?: boolean;
	min?: string;
	max?: string;
	maxLength?: number;
	clearable?: boolean;
	clearLabel?: string;
};

const BizInput = forwardRef<HTMLInputElement, BizInputProps>(
	(
		{
			onChange,
			onBlur,
			onKeyUp,
			value,
			placeholder,
			className,
			type: pType,
			formConfig,
			name: pName,
			readonly,
			legacy,
			min,
			max,
			maxLength,
			size,
			clearable = false,
			clearLabel = 'Clear',
		},
		ref
	) => {
		const [name] = useState(pName ?? getGuid());
		const inputRef = useRef<HTMLInputElement>(null);
		useImperativeHandle(ref, () => inputRef.current!, [clearable]);
		const isValid = formConfig?.state.fields[name]?.isValid ?? true;

		useEffect(() => {
			if (!formConfig || !formConfig.validators) return;

			const newIsValid = formConfig.state.validateFieldValue(value, formConfig.validators);
			if (newIsValid !== isValid) {
				formConfig.state.setFieldValid(name, newIsValid, formConfig.group);
			}
		}, [value, formConfig?.validators]);

		function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
			onChange(e);
			if (formConfig && !formConfig.state.isDirty) {
				formConfig.state.setDirty();
			}
		}

		function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
			if (onBlur) {
				onBlur(e);
			}
		}

		function handleKeyUp(e: React.KeyboardEvent<HTMLInputElement>) {
			if (onKeyUp) {
				onKeyUp(e);
			}
		}

		function handleClear() {
			const input = inputRef.current;
			if (!input || readonly) return;

			// Go through React's input change handler so the event contract and dirty state are preserved.
			const setValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')!.set!;
			setValue.call(input, '');
			input.dispatchEvent(new Event('input', { bubbles: true }));
			input.focus();
		}

		let currentClasses = `biz-input-surface ${className ?? ''}`;
		if (!isValid) {
			currentClasses += ' invalid';
		}

		if (!legacy) {
			currentClasses += ' biz-input';
		}

		const type = pType ?? 'text';

		const input = (
			<input
				ref={inputRef}
				type={type}
				className={currentClasses}
				data-biz-size={size}
				placeholder={placeholder}
				value={value}
				name={name}
				onChange={handleChange}
				readOnly={readonly}
				min={min}
				max={max}
				maxLength={maxLength}
				onBlur={handleBlur}
				onKeyUp={handleKeyUp}
			/>
		);

		if (!clearable) return input;

		return (
			<div className='biz-input-clearable'>
				{input}
				{value !== '' && !readonly && (
					<button
						type='button'
						className='biz-input-clearable__clear'
						onMouseDown={(e) => e.preventDefault()}
						onClick={handleClear}
						aria-label={clearLabel}
					>
						<i className='fa-regular fa-times' aria-hidden='true' />
					</button>
				)}
			</div>
		);
	}
);

// Set display name for debugging, important for HOCs and React DevTools
BizInput.displayName = 'BizInput';

export default BizInput;
